import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

const STATUS_COLORS = {
  open: 'badge-green', pending_close: 'badge-amber',
  pending_approval: 'badge-amber', closed: 'badge-grey', reopened: 'badge-red',
}
const STATUS_LABELS = {
  open: '🟢 Open', pending_close: '⏳ Pending Close',
  pending_approval: '⏳ Awaiting Approval', closed: '🔒 Closed', reopened: '🔓 Reopened',
}
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

export default function PeriodClose() {
  const { user, profile, isAdmin, isAdminOrManager } = useAuth()
  const [periods, setPeriods] = useState([])
  const [auditLog, setAuditLog] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [msg, setMsg] = useState(null)
  const [tab, setTab] = useState('periods')
  const [reopenModal, setReopenModal] = useState(null)
  const [reopenReason, setReopenReason] = useState('')

  const isSharonEmail = profile?.email?.includes('snankunda')
  const isEdwardEmail = profile?.email?.includes('elkatongole')
  const canReopen = isAdmin && (isSharonEmail || isEdwardEmail)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [periodsRes, auditRes] = await Promise.all([
      supabase.from('accounting_periods')
        .select('*, closer:profiles!closed_by(full_name), reopener:profiles!reopen_requested_by(full_name)')
        .order('period_year').order('period_month'),
      supabase.from('audit_log')
        .select('*, profiles(full_name)').eq('entity_type','accounting_period')
        .order('created_at', { ascending:false }).limit(50)
    ])
    setPeriods(periodsRes.data || [])
    setAuditLog(auditRes.data || [])
    setLoading(false)
  }

  const logAudit = async (event_type, entity_id, description, before_state, after_state) => {
    await supabase.from('audit_log').insert({
      event_type, entity_type:'accounting_period', entity_id,
      performed_by: user.id, description, before_state, after_state
    })
  }

  const notifyAdmins = async (period_label) => {
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/notify-period-close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`
        },
        body: JSON.stringify({
          period_label,
          submitted_by: profile?.full_name || profile?.email
        })
      })
    } catch (err) {
      console.error('Failed to send notification:', err)
    }
  }

  const [closeConfirmPeriod, setCloseConfirmPeriod] = React.useState(null)
  const [recloseConfirmPeriod, setRecloseConfirmPeriod] = React.useState(null)

  const handleRequestClose = async (period) => {
    setCloseConfirmPeriod(period)
  }

  const handleConfirmRequestClose = async () => {
    const period = closeConfirmPeriod
    setCloseConfirmPeriod(null)
    setSaving(period.id)
    await supabase.from('accounting_periods').update({
      status: 'pending_approval',
      closed_by: user.id,
      closed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }).eq('id', period.id)
    await logAudit('period_close_requested', period.id,
      `${profile.full_name} submitted ${period.period_label} for month-end close`,
      { status:'open' }, { status:'pending_approval' })
    // Send email notification to Edward and Sharon
    await notifyAdmins(period.period_label)
    setSaving(null); loadData()
    setMsg({ type:'success', text:`${period.period_label} submitted for approval. Edward and Sharon have been notified by email.` })
    setTimeout(() => setMsg(null), 5000)
  }

  const handleApprove = async (period) => {
    const sharonDone = isSharonEmail ? true : period.sharon_approved
    const edwardDone = isEdwardEmail ? true : period.edward_approved
    const bothApproved = sharonDone && edwardDone

    const updates = {
      ...(isSharonEmail ? { sharon_approved:true, sharon_approved_at:new Date().toISOString() } : {}),
      ...(isEdwardEmail ? { edward_approved:true, edward_approved_at:new Date().toISOString() } : {}),
      ...(bothApproved ? { status:'closed', updated_at:new Date().toISOString() } : {}),
    }

    setSaving(period.id)
    await supabase.from('accounting_periods').update(updates).eq('id', period.id)
    await logAudit('period_approved', period.id,
      `${profile.full_name} approved ${period.period_label}${bothApproved?' — PERIOD NOW CLOSED':''}`,
      { status:period.status }, { status: bothApproved?'closed':'pending_approval' })
    setSaving(null); loadData()
    setMsg({ type:'success', text: bothApproved
      ? `✅ ${period.period_label} is now permanently closed and locked.`
      : `Approval recorded. Waiting for ${isSharonEmail?'Edward':'Sharon'} to also approve.` })
    setTimeout(() => setMsg(null), 5000)
  }

  const handleRequestReopen = async () => {
    if (reopenReason.length < 50) {
      setMsg({ type:'error', text:'Please provide a detailed reason — minimum 50 characters.' }); return
    }
    setSaving(reopenModal.id)
    await supabase.from('accounting_periods').update({
      status: 'reopened',
      reopen_requested_by: user.id,
      reopen_reason: reopenReason,
      reopen_approved_by: user.id,
      reopen_approved_at: new Date().toISOString(),
      reopen_count: (reopenModal.reopen_count||0) + 1,
      sharon_approved: false, edward_approved: false,
      updated_at: new Date().toISOString()
    }).eq('id', reopenModal.id)
    await logAudit('period_reopened', reopenModal.id,
      `${profile.full_name} reopened ${reopenModal.period_label}: ${reopenReason}`,
      { status:'closed' }, { status:'reopened' })
    setSaving(null); setReopenModal(null); setReopenReason(''); loadData()
    setMsg({ type:'success', text:`${reopenModal.period_label} reopened. Make corrections using reversing entries, then re-close.` })
    setTimeout(() => setMsg(null), 5000)
  }

  const handleReclose = async (period) => {
    setRecloseConfirmPeriod(period)
  }

  const handleConfirmReclose = async () => {
    const period = recloseConfirmPeriod
    setRecloseConfirmPeriod(null)
    setSaving(period.id)
    await supabase.from('accounting_periods').update({
      status: 'pending_approval',
      sharon_approved: false, edward_approved: false,
      updated_at: new Date().toISOString()
    }).eq('id', period.id)
    await logAudit('period_reclose_requested', period.id,
      `${profile.full_name} re-submitted ${period.period_label} for close after corrections`,
      { status:'reopened' }, { status:'pending_approval' })
    await notifyAdmins(period.period_label)
    setSaving(null); loadData()
    setMsg({ type:'success', text:`${period.period_label} re-submitted. Edward and Sharon notified.` })
    setTimeout(() => setMsg(null), 3000)
  }

  const handleCreateNextMonth = async () => {
    const monthPeriods = periods.filter(p => p.period_type==='month')
    const lastPeriod = monthPeriods.slice(-1)[0]
    if (!lastPeriod) return
    const nextMonth = (lastPeriod.period_month % 12) + 1
    const nextYear = lastPeriod.period_year + (lastPeriod.period_month===12?1:0)
    const startDate = `${nextYear}-${String(nextMonth).padStart(2,'0')}-01`
    const endDate = new Date(nextYear, nextMonth, 0).toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
    const { error } = await supabase.from('accounting_periods').insert({
      period_type:'month', period_year:nextYear, period_month:nextMonth,
      period_label:`${MONTHS[nextMonth-1]} ${nextYear}`,
      start_date:startDate, end_date:endDate, status:'open'
    })
    if (!error) { loadData(); setMsg({ type:'success', text:`${MONTHS[nextMonth-1]} ${nextYear} period opened.` }) }
    setTimeout(() => setMsg(null), 3000)
  }

  return (
    <AppLayout title="Period Close">
      <div className="section-header">
        <div className="section-icon">🔒</div>
        <div className="section-info">
          <h2>Accounting Period Close</h2>
          <p>Manage month-end close. Closed periods are permanent. Edward or Sharon can reopen for corrections.</p>
        </div>
        {isAdminOrManager && (
          <button className="btn btn-secondary" style={{ marginLeft:'auto' }} onClick={handleCreateNextMonth}>
            + Open Next Month
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="alert alert-amber">
        <strong>Workflow:</strong> Finance Manager submits close → Email sent to Edward &amp; Sharon → Both approve → Period locked permanently. Errors after close: Edward or Sharon reopens (reason required, audit logged) → corrections via reversing entries → re-close.
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='periods'?'active':''}`} onClick={() => setTab('periods')}>📅 Periods</button>
        <button className={`tab ${tab==='audit'?'active':''}`} onClick={() => setTab('audit')}>📋 Audit Log</button>
      </div>

      {tab==='periods' && (
        <div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : periods.length===0 ? <div className="empty-state"><div className="empty-icon">📅</div><h3>No periods yet</h3></div>
          : periods.map(period => (
            <div key={period.id} className="card" style={{ marginBottom:12, borderLeft:`4px solid ${
              period.status==='closed'?'var(--border)':period.status==='reopened'?'var(--red)':
              period.status==='pending_approval'?'var(--amber)':'var(--green-bright)'}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:700, color:'var(--green-deep)' }}>{period.period_label}</span>
                    <span className={`badge ${STATUS_COLORS[period.status]}`}>{STATUS_LABELS[period.status]}</span>
                    {period.reopen_count>0 && <span className="badge badge-red">Reopened {period.reopen_count}×</span>}
                  </div>
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                    {new Date(period.start_date).toLocaleDateString('en-UG')} — {new Date(period.end_date).toLocaleDateString('en-UG')}
                    {period.closer?.full_name && ` · Submitted by ${period.closer.full_name}`}
                  </div>
                </div>
                {period.status==='closed' && <span style={{ fontSize:'1.5rem' }}>🔒</span>}
                {period.status==='reopened' && <span style={{ fontSize:'1.5rem' }}>🔓</span>}
              </div>

              {/* Reopen reason */}
              {period.reopen_reason && (
                <div style={{ fontSize:'0.75rem', color:'var(--red)', fontStyle:'italic', marginBottom:10, padding:'8px 10px', background:'var(--red-pale)', borderRadius:6 }}>
                  Reopen reason: {period.reopen_reason}
                </div>
              )}

              {/* Approval status */}
              {['pending_approval','closed'].includes(period.status) && (
                <div style={{ display:'flex', gap:16, padding:'10px 12px', background:'var(--cream)', borderRadius:8, marginBottom:12, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.82rem' }}>
                    {period.sharon_approved?'✅':'⬜'}
                    <span style={{ fontWeight:600 }}>Sharon</span>
                    {period.sharon_approved && <span style={{ color:'var(--text-muted)', fontSize:'0.7rem' }}>Approved {period.sharon_approved_at?new Date(period.sharon_approved_at).toLocaleDateString('en-UG'):''}</span>}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.82rem' }}>
                    {period.edward_approved?'✅':'⬜'}
                    <span style={{ fontWeight:600 }}>Edward</span>
                    {period.edward_approved && <span style={{ color:'var(--text-muted)', fontSize:'0.7rem' }}>Approved {period.edward_approved_at?new Date(period.edward_approved_at).toLocaleDateString('en-UG'):''}</span>}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {period.status==='open' && isAdminOrManager && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleRequestClose(period)} disabled={saving===period.id}>
                    {saving===period.id?<><span className="spinner" />...</>:'🔒 Submit for Close'}
                  </button>
                )}
                {period.status==='pending_approval' && isSharonEmail && !period.sharon_approved && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleApprove(period)} disabled={saving===period.id}>
                    ✅ Sharon — Approve
                  </button>
                )}
                {period.status==='pending_approval' && isEdwardEmail && !period.edward_approved && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleApprove(period)} disabled={saving===period.id}>
                    ✅ Edward — Approve
                  </button>
                )}
                {period.status==='pending_approval' && !isSharonEmail && !isEdwardEmail && (
                  <span style={{ fontSize:'0.78rem', color:'var(--amber)', fontStyle:'italic' }}>Waiting for Edward and Sharon to approve</span>
                )}
                {period.status==='closed' && canReopen && (
                  <button className="btn btn-danger btn-sm" onClick={() => { setReopenModal(period); setReopenReason('') }}>
                    🔓 Reopen for Correction
                  </button>
                )}
                {period.status==='reopened' && isAdminOrManager && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleReclose(period)} disabled={saving===period.id}>
                    🔒 Re-close Period
                  </button>
                )}
                {period.status==='closed' && (
                  <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', fontStyle:'italic', padding:'6px 0' }}>
                    All entries permanently locked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==='audit' && (
        <div className="card">
          <div className="card-title">Audit Log</div>
          <div className="card-subtitle">Permanent record of all period close actions. Cannot be edited or deleted.</div>
          {auditLog.length===0
            ? <div className="empty-state"><div className="empty-icon">📋</div><h3>No audit events yet</h3></div>
            : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Date & Time</th><th>Event</th><th>By</th><th>Description</th></tr></thead>
                  <tbody>
                    {auditLog.map(log => (
                      <tr key={log.id}>
                        <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.72rem', whiteSpace:'nowrap' }}>
                          {new Date(log.created_at).toLocaleString('en-UG')}
                        </td>
                        <td><span className="badge badge-grey" style={{ fontSize:'0.65rem' }}>{log.event_type.replace(/_/g,' ')}</span></td>
                        <td style={{ fontWeight:600, fontSize:'0.82rem' }}>{log.profiles?.full_name||'System'}</td>
                        <td style={{ fontSize:'0.78rem' }}>{log.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* Reopen Modal */}
      {reopenModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setReopenModal(null)}>
          <div className="modal">
            <div className="modal-title" style={{ color:'var(--red)' }}>🔓 Reopen {reopenModal.period_label}</div>
            <div className="modal-subtitle">
              This will unlock the period for corrections. All corrections must be made as reversing journal entries — originals cannot be deleted. The period must then be re-closed and re-approved.
            </div>
            <div className="form-group">
              <label className="form-label">Reason for reopening * (minimum 50 characters)</label>
              <textarea className="form-textarea" style={{ minHeight:100 }}
                placeholder="Describe the error found and what corrections will be made..."
                value={reopenReason} onChange={e => setReopenReason(e.target.value)} />
              <div style={{ fontSize:'0.72rem', color:reopenReason.length>=50?'var(--green-mid)':'var(--text-muted)', marginTop:4 }}>
                {reopenReason.length}/50 characters minimum
              </div>
            </div>
            <div className="alert alert-red">
              ⚠️ This action is permanently recorded in the audit log with your name, timestamp, and reason.
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setReopenModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRequestReopen} disabled={!!saving||reopenReason.length<50}>
                {saving?<><span className="spinner" />...</>:'🔓 Confirm Reopen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {closeConfirmPeriod && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setCloseConfirmPeriod(null)}>
          <div className="modal">
            <div className="modal-title">🔒 Submit for Month-End Close</div>
            <div className="modal-subtitle">Submit <strong>{closeConfirmPeriod.period_label}</strong> for review? Edward and Sharon will be notified by email to approve.</div>
            <div className="alert alert-amber">⚠️ Once submitted, no more entries can be made for this period until approved or rejected.</div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setCloseConfirmPeriod(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmRequestClose}>🔒 Submit for Close</button>
            </div>
          </div>
        </div>
      )}

      {recloseConfirmPeriod && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setRecloseConfirmPeriod(null)}>
          <div className="modal">
            <div className="modal-title">🔄 Re-Submit for Close</div>
            <div className="modal-subtitle">Re-submit <strong>{recloseConfirmPeriod.period_label}</strong> after corrections?</div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setRecloseConfirmPeriod(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmReclose}>🔄 Re-Submit</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
